import React, { useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { Category } from "../CategoryMegaMenu";
import { buildCategoryPath } from "../../utils/categoryPathUtils";
import { Button } from "../../shadcn/ui/Button";

export interface CategoryMegaPanelProps {
  category: Category;
  onNavigate: (path: string) => void;
  searchQuery?: string;
}

export const CategoryMegaPanel: React.FC<CategoryMegaPanelProps> = ({
  category,
  onNavigate,
  searchQuery = "",
}) => {
  const filterSubcategories = (subcategories: Category[]): Category[] => {
    if (!searchQuery.trim()) return subcategories;

    const query = searchQuery.toLowerCase().trim();

    return subcategories.filter((subcat) => {
      const nameMatches = subcat.name.toLowerCase().includes(query);

      const hasMatchingChildren =
        subcat.children?.some((level3) =>
          level3.name.toLowerCase().includes(query)
        ) || false;

      return nameMatches || hasMatchingChildren;
    });
  };

  const filteredChildren = useMemo(() => {
    if (!category.children) return [];
    return filterSubcategories(category.children);
  }, [category.children, searchQuery]);

  const hasChildren = category.children?.length && category.children.length > 0;
  const hasFilteredChildren = filteredChildren.length > 0;

  const hasLevel3 =
    hasChildren &&
    category.children!.some(
      (level2) => level2.children && level2.children.length > 0
    );

  // ============================
  // CASE 1: Has Level 3
  // ============================
  if (hasLevel3) {
    if (searchQuery.trim() && !hasFilteredChildren) {
      return (
        <div className="p-4 sm:p-6">
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No subcategories found</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 sm:p-6">
        {/* ✅ Responsive Grid */}
        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {filteredChildren.map((level2) => {
            const hasLevel3Children =
              level2.children && level2.children.length > 0;

            return (
              <div key={level2.id} className="flex flex-col">
                {/* Level-2 */}
                <div
                  className="mb-3 cursor-pointer group"
                  onClick={() => {
                    if (!hasLevel3Children) {
                      const path = buildCategoryPath([category, level2]);
                      onNavigate(
                        `/search?categoryPath=${encodeURIComponent(path)}`
                      );
                    }
                  }}
                >
                  <h3 className="text-sm font-semibold text-gray-900 group-hover:text-[#F97316] transition-colors">
                    {level2.name}
                    {!hasLevel3Children && (
                      <ChevronRight className="inline-block h-4 w-4 ml-1 text-gray-400 group-hover:text-[#F97316]" />
                    )}
                  </h3>
                </div>

                {/* Level-3 */}
                {hasLevel3Children &&
                  (() => {
                    const filteredLevel3 = searchQuery.trim()
                      ? level2.children!.filter((level3) =>
                          level3.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        )
                      : level2.children!;

                    const level2Matches = searchQuery.trim()
                      ? level2.name
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      : false;

                    if (
                      searchQuery.trim() &&
                      filteredLevel3.length === 0 &&
                      !level2Matches
                    ) {
                      return null;
                    }

                    const level3ToShow = level2Matches
                      ? level2.children!
                      : filteredLevel3;

                    return (
                      <ul className="space-y-2">
                        {level3ToShow.map((level3) => (
                          <li key={level3.id}>
                            <button
                              onClick={() => {
                                const path = buildCategoryPath([
                                  category,
                                  level2,
                                  level3,
                                ]);
                                onNavigate(
                                  `/search?categoryPath=${encodeURIComponent(path)}`
                                );
                              }}
                              className="text-sm text-gray-600 hover:text-[#F97316] transition-colors flex justify-between w-full group"
                            >
                              <span>{level3.name}</span>
                              <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
              </div>
            );
          })}
        </div>

        {/* Button */}
      </div>
    );
  }

  // ============================
  // CASE 2: Only Level 2
  // ============================
  if (hasChildren) {
    if (searchQuery.trim() && !hasFilteredChildren) {
      return (
        <div className="p-4 sm:p-6">
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No subcategories found</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 sm:p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {category.name}
        </h2>
        <div
          className="grid gap-x-6 gap-y-2"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          }}
        >
          {filteredChildren.map((level2) => (
            <button
              key={level2.id}
              onClick={() => {
                const path = buildCategoryPath([category, level2]);
                onNavigate(`/search?categoryPath=${encodeURIComponent(path)}`);
              }}
              className="group flex items-center justify-between gap-2 py-1.5 text-left text-sm text-gray-700 hover:text-[#F97316] transition-colors"
            >
              <span className="truncate">{level2.name}</span>
              <ChevronRight className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
            </button>
          ))}
        </div>

      </div>
    );
  }

  // ============================
  // CASE 3: No children
  // ============================
  return (
    <div className="p-4 sm:p-6">
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">
          Browse all products in {category.name}
        </p>
        <Button
          onClick={() => {
            const path = buildCategoryPath([category]);
            onNavigate(`/search?categoryPath=${encodeURIComponent(path)}`);
          }}
          className="bg-[#F97316] hover:bg-[#ea6820] text-white"
        >
          Browse {category.name}
        </Button>
      </div>

    </div>
  );
};